// client/src/utils/csrfToken.ts
//
// Client half of the server's double-submit CSRF scheme.
//
// The server (server/index.js) issues an HttpOnly `csrf_secret` cookie and
// echoes a matching token on EVERY response as the `X-CSRF-Token` header. It
// then demands that token back, as `x-csrf-token`, on state-changing requests
// that authenticate via an ambient cookie.
//
// Before the HttpOnly migration nothing here was needed: every authenticated
// call carried an `Authorization` header, and the server exempts bearer-auth
// requests from CSRF (a header credential is not sent ambiently, so those
// requests aren't forgeable). After the migration the session rides on the
// HttpOnly `auth_session` cookie, so a hard navigation - which wipes the
// in-memory access token - leaves calls cookie-authenticated with no bearer
// header, and the server correctly started demanding a CSRF token the client
// never sent. Every POST then failed with 403 "Invalid or missing CSRF token".
//
// So: cache the token off each response, send it on state-changing requests,
// and prime it from `/health` when a mutation happens before any response
// has been seen (e.g. the dashboard's POST /api/batch on a cold load).

let cachedToken: string | null = null;
let inflightPrime: Promise<string | null> | null = null;

const CSRF_HEADER = 'x-csrf-token';
const SAFE_METHODS = new Set(['get', 'head', 'options']);

export function isStateChangingMethod(method: string | undefined): boolean {
  return !SAFE_METHODS.has((method ?? 'get').toLowerCase());
}

/** Cache the token echoed on a response. Accepts any header-bag shape. */
export function captureCsrfToken(headers: unknown): void {
  if (!headers) return;

  let value: unknown;
  if (typeof (headers as { get?: unknown }).get === 'function') {
    value = (headers as { get(name: string): unknown }).get(CSRF_HEADER);
  } else {
    const bag = headers as Record<string, unknown>;
    value = bag[CSRF_HEADER] ?? bag['X-CSRF-Token'];
  }

  if (typeof value === 'string' && value) {
    cachedToken = value;
  }
}

export function getCsrfToken(): string | null {
  return cachedToken;
}

export function clearCsrfToken(): void {
  cachedToken = null;
}

/**
 * The token, fetching one from `/health` if none has been seen yet.
 * `/health` is a plain GET, so it is itself exempt from the CSRF check, and
 * the token middleware runs ahead of every route - so the response carries a
 * token whatever the endpoint returns. Concurrent callers share one request.
 */
export async function ensureCsrfToken(): Promise<string | null> {
  if (cachedToken) return cachedToken;
  if (typeof window === 'undefined') return null;
  if (inflightPrime) return inflightPrime;

  const prime = (async () => {
    try {
      const response = await fetch('/health', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
      captureCsrfToken(response.headers);
    } catch (err) {
      // Offline or the API is down - the caller proceeds without a token and
      // takes the server's 403, which is the pre-existing behavior.
      console.warn('[CSRF] Could not prime token:', err);
    }
    return cachedToken;
  })();
  inflightPrime = prime;
  // Cleared asynchronously, after the assignment above. A `finally` inside the
  // IIFE runs synchronously when `fetch` throws before its first await, which
  // cleared the slot before it was set and left a settled promise in it for
  // good - every later prime then returned null without fetching.
  void prime.finally(() => {
    if (inflightPrime === prime) inflightPrime = null;
  });

  return prime;
}

/** True for relative URLs and absolute URLs on this page's origin. */
function isSameOrigin(url: string | undefined): boolean {
  if (!url) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

type CsrfInterceptable = {
  interceptors: {
    request: { use: (onFulfilled: (config: any) => any) => number };
    response: { use: (onFulfilled: (r: any) => any, onRejected: (e: any) => any) => number };
  };
  request: (config: any) => Promise<unknown>;
};

const installed = new WeakSet<object>();

/**
 * Give a plain axios instance the same CSRF handling `monitoredAxios` has:
 * send the cached token on same-origin state-changing requests, keep the
 * rotated token from every response, and retry once when the server rejects
 * a stale one.
 *
 * Needed for the default `axios` export. A dozen call sites (every onboarding
 * step among them) mutate through it, and after a hard navigation - e.g. the
 * Google sign-in landing on /dashboard - there is no bearer header, so the
 * request is cookie-authenticated and the server demands the token. Without
 * it, PUT /api/portfolio-settings 403'd and "Continue to Add Your Savings"
 * silently did nothing.
 */
export function installCsrfInterceptors(instance: CsrfInterceptable): void {
  // Not an axios instance (e.g. the module mock in unit tests): nothing to do.
  if (!instance?.interceptors?.request?.use || !instance?.interceptors?.response?.use) return;
  if (installed.has(instance)) return;
  installed.add(instance);

  const withToken = (config: any, token: string | null) => {
    if (token) {
      if (typeof config.headers?.set === 'function') {
        config.headers.set(CSRF_HEADER, token);
      } else {
        config.headers = { ...(config.headers || {}), [CSRF_HEADER]: token };
      }
    }
    return config;
  };

  // A cached token is attached synchronously. On a cold miss, wait for the
  // in-flight prime rather than sending a request that is certain to 403.
  instance.interceptors.request.use((config) => {
    if (!isStateChangingMethod(config.method) || !isSameOrigin(config.url)) return config;
    const cached = getCsrfToken();
    if (cached) return withToken(config, cached);
    return ensureCsrfToken().then((token) => withToken(config, token));
  });

  instance.interceptors.response.use(
    (response) => {
      captureCsrfToken(response?.headers);
      return response;
    },
    async (error) => {
      const response = error?.response;
      if (response?.headers) captureCsrfToken(response.headers);

      const config = error?.config;
      const message = response?.data?.error;
      if (
        response?.status === 403 &&
        config &&
        !config._retryCsrf &&
        isStateChangingMethod(config.method) &&
        isSameOrigin(config.url) &&
        typeof message === 'string' &&
        message.toLowerCase().includes('csrf')
      ) {
        config._retryCsrf = true;
        const fresh = getCsrfToken() ?? (await ensureCsrfToken());
        if (fresh) {
          if (typeof config.headers?.set === 'function') {
            config.headers.set(CSRF_HEADER, fresh);
          } else {
            config.headers = { ...(config.headers || {}), [CSRF_HEADER]: fresh };
          }
          return instance.request(config);
        }
      }
      return Promise.reject(error);
    }
  );
}

// Prime the token as soon as this module loads in the browser, so the first
// mutation of a page load finds one cached and never has to block on the
// `/health` round trip. Fire-and-forget: `ensureCsrfToken` swallows its own
// errors, and a mutation that still beats it simply awaits the same promise.
if (typeof window !== 'undefined') {
  void ensureCsrfToken();
}

export { CSRF_HEADER };
